import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  humanizarPergunta,
  ROTULO_CANAL,
  ROTULO_ESTADO_SLA,
  ROTULO_ETAPA,
  ROTULO_SCORE,
  ROTULO_STATUS_IMPLANTACAO,
  ROTULO_STATUS_MOVIMENTACAO,
  ROTULO_STATUS_RENOVACAO,
  ROTULO_STATUS_REVISAO,
  ROTULO_TIPO_CONTATO,
  ROTULO_TIPO_MOVIMENTACAO,
  TETO_DAS_OBSERVACOES,
  formatarValorMensal,
  type CanalContato,
  type EstadoSla,
  type EtapaFunil,
  type OrigemLead,
  type Score,
  type StatusImplantacao,
  type StatusMovimentacao,
  type StatusRenovacao,
  type StatusRevisao,
  type TipoContato,
  type TipoMovimentacao,
} from '@reducee/contracts';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';

import { ErroDaApi, chamarApi } from '../api/cliente.ts';
import { useSessao } from '../autenticacao/sessao.tsx';
import {
  BeneficiariosDoContato,
  type BeneficiarioDoContato,
} from '../componentes/BeneficiariosDoContato.tsx';
import { CampoTextoLongo } from '../componentes/Campo.tsx';
import {
  DocumentosDoContato,
  type DocumentoDoContato,
} from '../componentes/DocumentosDoContato.tsx';
import { useOrigens } from '../dominio/origens.ts';

type Visao360 = {
  contato: {
    id: string;
    nome: string;
    tipo: TipoContato;
    documento: string | null;
    telefone: string | null;
    whatsapp: string | null;
    email: string | null;
    observacoes: string | null;
    naoContatar: boolean;
    ehCliente: boolean;
    criadoEm: string;
  };
  leads: Array<{
    id: string;
    origem: OrigemLead;
    campanha: string | null;
    dataEntrada: string;
    estadoSla: EstadoSla;
    resgatadoEm: string | null;
    responsavel: string;
    respostasDoFormulario: Array<{ pergunta: string; respostas: string[] }>;
  }>;
  oportunidades: Array<{
    id: string;
    produto: string;
    etapa: EtapaFunil;
    score: Score;
    valorMensalCentavos: number;
    proximoPasso: string | null;
    dataProximoPasso: string | null;
    responsavel: string;
  }>;
  contratos: Array<{
    id: string;
    numero: string | null;
    produto: string;
    operadora: string | null;
    /** Contrato herdado da carteira: sem Implantação, fora da produção (ADR 0015). */
    herdado: boolean;
    valorMensalCentavos: number;
    diaVencimento: number | null;
    vigenciaInicio: string | null;
    criadoEm: string;
    implantacao: {
      id: string;
      status: StatusImplantacao;
      pagamentoPrimeiroEm: string | null;
      checklist: { concluidos: number; total: number };
    } | null;
    parcelas: { pagas: number; total: number; vencidas: number };
    renovacao: {
      id: string;
      status: StatusRenovacao;
      statusRevisao: StatusRevisao;
      dataRevisao: string | null;
      dataRenovacao: string | null;
    } | null;
  }>;
  beneficiarios: BeneficiarioDoContato[];
  movimentacoes: Array<{
    id: string;
    tipo: TipoMovimentacao;
    status: StatusMovimentacao;
    protocolo: string | null;
    abertaEm: string;
    prazo: string | null;
  }>;
  documentos: DocumentoDoContato[];
  tarefas: Array<{ id: string; titulo: string; status: string; prazo: string | null; responsavel: string }>;
  cadencias: Array<{ id: string; status: string; modelo: string; pendentes: number; total: number }>;
  historico: Array<{
    id: string;
    canal: CanalContato;
    sentido: 'SAIDA' | 'ENTRADA';
    houveResposta: boolean;
    descricao: string | null;
    ocorridaEm: string;
    usuaria: string;
  }>;
};

const ROTULO_STATUS_CADENCIA: Record<string, string> = {
  ATIVA: 'Ativa',
  PAUSADA: 'Pausada',
  CONCLUIDA: 'Concluída',
  CANCELADA: 'Cancelada',
};

const ROTULO_STATUS_TAREFA: Record<string, string> = {
  A_FAZER: 'A fazer',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDA: 'Concluída',
};

/**
 * Jornada comercial completa de um Contato.
 *
 * Mantém a distinção do vocabulário à vista: a identidade no topo, as entradas
 * comerciais, as tentativas de venda e, só quando existe Contrato, a condição
 * de Cliente.
 */
export function PaginaVisao360(): React.JSX.Element {
  const { contatoId } = useParams();
  const { usuaria } = useSessao();
  const origens = useOrigens();

  // A lista de Contatos é da Administradora (NEX-222); quem não é chega aqui
  // pela busca do topo, e o caminho de volta é o Sales.
  const volta =
    usuaria?.papel === 'ADMINISTRADORA'
      ? { para: '/contatos', rotulo: 'Contatos' }
      : { para: '/sales', rotulo: 'Sales' };

  const consulta = useQuery({
    queryKey: ['visao-360', contatoId],
    queryFn: () => chamarApi<Visao360>(`/contatos/${contatoId}/visao-360`),
    enabled: Boolean(contatoId),
  });

  if (consulta.isError) {
    const causa = consulta.error;
    return (
      <section>
        <p role="alert" className="rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {causa instanceof ErroDaApi ? causa.message : 'Não foi possível abrir este Contato.'}
        </p>
        <Link to={volta.para} className="mt-4 inline-block text-sm text-informacao underline">
          Voltar para {volta.rotulo}
        </Link>
      </section>
    );
  }

  const dados = consulta.data;
  if (!dados) return <p className="text-texto-suave">Carregando…</p>;

  return (
    <section data-teste="visao-360">
      <Link to={volta.para} className="text-sm text-texto-suave underline">
        {volta.rotulo}
      </Link>

      <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
        {/* `min-w-0` deixa a coluna encolher abaixo do editor de observações:
          * sem ele, o formulário de 560px empurrava a página para fora de um
          * celular de 390px. */}
        <div className="min-w-0 max-w-full">
          <h1 className="text-xl font-semibold text-marinho">{dados.contato.nome}</h1>
          <p className="text-texto-suave">
            {ROTULO_TIPO_CONTATO[dados.contato.tipo]}
            {dados.contato.documento ? ` · ${dados.contato.documento}` : ''}
          </p>
          <p className="text-texto-suave">
            {[dados.contato.telefone, dados.contato.whatsapp, dados.contato.email]
              .filter(Boolean)
              .join(' · ') || 'Sem meio de contato registrado'}
          </p>
          <ObservacoesDoContato
            contatoId={dados.contato.id}
            observacoes={dados.contato.observacoes}
          />
        </div>

        <div className="flex flex-col items-end gap-1">
          <span
            data-teste="situacao-contato"
            className={`rounded-full px-3 py-1 text-sm font-medium ${
              dados.contato.ehCliente
                ? 'bg-sucesso/10 text-sucesso'
                : 'bg-marinho-suave text-marinho'
            }`}
          >
            {dados.contato.ehCliente ? 'Cliente' : 'Ainda não é Cliente'}
          </span>
          {dados.contato.naoContatar ? (
            <span className="rounded-full bg-erro/10 px-3 py-1 text-xs font-medium text-erro">
              Não contatar
            </span>
          ) : null}
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Bloco teste="visao-360-leads" titulo="Entradas comerciais" vazio="Nenhum Lead registrado.">
          {dados.leads.map((lead) => (
            <li key={lead.id} className="rounded-lg bg-marinho-suave px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">{origens.nome(lead.origem)}</span>
                <span className="text-xs text-texto-suave">
                  {new Date(lead.dataEntrada).toLocaleDateString('pt-BR')}
                </span>
              </div>
              <p className="text-xs text-texto-suave">
                {lead.campanha ? `${lead.campanha} · ` : ''}
                {ROTULO_ESTADO_SLA[lead.estadoSla]} · {lead.responsavel}
              </p>
              {/*
                A data do resgate fica à parte do estado de SLA de propósito:
                quem investiga um Lead de semanas atrás na lista de hoje
                precisa saber quando ele foi trazido, e o estado para de
                responder isso depois do primeiro contato.
              */}
              {lead.resgatadoEm ? (
                <p className="text-xs text-texto-suave">
                  Resgatado do Lead Center em{' '}
                  {new Date(lead.resgatadoEm).toLocaleDateString('pt-BR')}
                </p>
              ) : null}
              {/*
                O que a pessoa respondeu no formulário de origem. É com isto
                que a Comercial abre a conversa — quanto ela paga hoje, quantas
                vidas entram, que hospital quer na rede. Lead de importação ou
                criado à mão não teve formulário e não mostra nada.
              */}
              {lead.respostasDoFormulario.length > 0 ? (
                <dl
                  data-teste="lead-respostas-formulario"
                  className="mt-2 space-y-1 border-t border-linha pt-2"
                >
                  {lead.respostasDoFormulario.map((resposta) => (
                    <div key={resposta.pergunta} className="text-xs">
                      <dt className="text-texto-suave">{humanizarPergunta(resposta.pergunta)}</dt>
                      <dd className="font-medium">{resposta.respostas.join(', ')}</dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </li>
          ))}
        </Bloco>

        <Bloco
          teste="visao-360-oportunidades"
          titulo="Tentativas de venda"
          vazio="Nenhuma Oportunidade registrada."
        >
          {dados.oportunidades.map((oportunidade) => (
            <li key={oportunidade.id} className="rounded-lg bg-marinho-suave px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">{oportunidade.produto}</span>
                <span className="text-xs font-medium">
                  {formatarValorMensal(oportunidade.valorMensalCentavos)}
                </span>
              </div>
              <p className="text-xs text-texto-suave">
                {ROTULO_ETAPA[oportunidade.etapa]} · {ROTULO_SCORE[oportunidade.score]} ·{' '}
                {oportunidade.responsavel}
              </p>
              {oportunidade.proximoPasso ? (
                <p className="mt-0.5 text-xs">
                  Próximo passo: {oportunidade.proximoPasso}
                  {oportunidade.dataProximoPasso
                    ? ` (${new Date(oportunidade.dataProximoPasso).toLocaleDateString('pt-BR')})`
                    : ''}
                </p>
              ) : null}
            </li>
          ))}
        </Bloco>

        <Bloco
          teste="visao-360-contratos"
          titulo="Contratos"
          vazio="Nenhum Contrato — por isso o Contato ainda não é Cliente."
        >
          {dados.contratos.map((contrato) => (
            <li key={contrato.id} className="rounded-lg bg-marinho-suave px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">
                  {contrato.produto}
                  {contrato.operadora ? ` · ${contrato.operadora}` : ''}
                </span>
                <span className="text-xs font-medium">
                  {formatarValorMensal(contrato.valorMensalCentavos)}
                </span>
              </div>
              <p className="text-xs text-texto-suave">
                {contrato.numero ? `Nº ${contrato.numero} · ` : ''}
                Vigência{' '}
                {contrato.vigenciaInicio
                  ? new Date(contrato.vigenciaInicio).toLocaleDateString('pt-BR')
                  : 'a definir'}
                {contrato.diaVencimento ? ` · vence dia ${contrato.diaVencimento}` : ''}
              </p>
              {contrato.herdado ? (
                <p className="text-xs text-texto-suave">
                  Herdado da carteira: sem implantação e fora da produção.
                </p>
              ) : null}

              {contrato.implantacao ? (
                <p className="mt-1 text-xs">
                  Implantação: {ROTULO_STATUS_IMPLANTACAO[contrato.implantacao.status]} ·
                  checklist {contrato.implantacao.checklist.concluidos}/
                  {contrato.implantacao.checklist.total} ·{' '}
                  {contrato.implantacao.pagamentoPrimeiroEm
                    ? `1º boleto pago em ${new Date(contrato.implantacao.pagamentoPrimeiroEm).toLocaleDateString('pt-BR')}`
                    : '1º boleto pendente'}
                </p>
              ) : null}

              {contrato.parcelas.total > 0 ? (
                <p className={`text-xs ${contrato.parcelas.vencidas > 0 ? 'text-erro' : 'text-texto-suave'}`}>
                  Parcelas: {contrato.parcelas.pagas}/{contrato.parcelas.total} quitadas
                  {contrato.parcelas.vencidas > 0
                    ? ` · ${contrato.parcelas.vencidas} vencida(s)`
                    : ''}
                </p>
              ) : null}

              {contrato.renovacao ? (
                <p className="text-xs text-texto-suave">
                  Renovação {ROTULO_STATUS_RENOVACAO[contrato.renovacao.status]} · revisão{' '}
                  {ROTULO_STATUS_REVISAO[contrato.renovacao.statusRevisao]}
                  {contrato.renovacao.dataRevisao
                    ? ` em ${new Date(contrato.renovacao.dataRevisao).toLocaleDateString('pt-BR')}`
                    : ''}
                </p>
              ) : null}

              {usuaria?.papel === 'ADMINISTRADORA' ? (
                <EditarContrato contatoId={dados.contato.id} contrato={contrato} />
              ) : null}
            </li>
          ))}
        </Bloco>

        <BeneficiariosDoContato
          contatoId={dados.contato.id}
          beneficiariosIniciais={dados.beneficiarios}
          contratos={dados.contratos}
        />

        <Bloco
          teste="visao-360-movimentacoes"
          titulo="Movimentações"
          vazio="Nenhuma movimentação registrada."
        >
          {dados.movimentacoes.map((movimentacao) => (
            <li key={movimentacao.id} className="rounded-lg bg-marinho-suave px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">
                  {ROTULO_TIPO_MOVIMENTACAO[movimentacao.tipo]}
                </span>
                <span className="text-xs">
                  {ROTULO_STATUS_MOVIMENTACAO[movimentacao.status]}
                </span>
              </div>
              <p className="text-xs text-texto-suave">
                Aberta em {new Date(movimentacao.abertaEm).toLocaleDateString('pt-BR')}
                {movimentacao.protocolo ? ` · protocolo ${movimentacao.protocolo}` : ''}
              </p>
            </li>
          ))}
        </Bloco>

        <DocumentosDoContato
          contatoId={dados.contato.id}
          documentosIniciais={dados.documentos}
          permitirEnvio
          // A pasta do Cliente também diz de quem é: o documento pode entrar
          // por aqui, fora da Implantação e da Movimentação, e continuar sendo
          // de uma vida específica.
          vidas={dados.beneficiarios
            .filter((beneficiario) => beneficiario.ativo)
            .map((beneficiario) => ({ id: beneficiario.id, nome: beneficiario.nome }))}
        />

        <Bloco teste="visao-360-cadencias" titulo="Cadências" vazio="Nenhuma cadência iniciada.">
          {dados.cadencias.map((cadencia) => (
            <li key={cadencia.id} className="rounded-lg bg-marinho-suave px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">{cadencia.modelo}</span>
                <span className="text-xs">{ROTULO_STATUS_CADENCIA[cadencia.status]}</span>
              </div>
              <p className="text-xs text-texto-suave">
                {cadencia.total - cadencia.pendentes} de {cadencia.total} tentativas tratadas
              </p>
            </li>
          ))}
        </Bloco>

        <Bloco teste="visao-360-tarefas" titulo="Tarefas" vazio="Nenhuma tarefa registrada.">
          {dados.tarefas.map((tarefa) => (
            <li key={tarefa.id} className="rounded-lg bg-marinho-suave px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">{tarefa.titulo}</span>
                <span className="text-xs">{ROTULO_STATUS_TAREFA[tarefa.status] ?? tarefa.status}</span>
              </div>
              <p className="text-xs text-texto-suave">
                {tarefa.prazo ? new Date(tarefa.prazo).toLocaleDateString('pt-BR') : 'Sem prazo'} ·{' '}
                {tarefa.responsavel}
              </p>
            </li>
          ))}
        </Bloco>

        <Bloco teste="visao-360-historico" titulo="Histórico" vazio="Nenhuma interação registrada.">
          {dados.historico.map((interacao) => (
            <li key={interacao.id} className="rounded-lg bg-marinho-suave px-3 py-2 text-sm">
              <div className="flex justify-between gap-2">
                <span className="font-medium">{ROTULO_CANAL[interacao.canal]}</span>
                <span className="text-xs text-texto-suave">
                  {new Date(interacao.ocorridaEm).toLocaleDateString('pt-BR')}
                </span>
              </div>
              {interacao.descricao ? <p className="text-xs">{interacao.descricao}</p> : null}
              <p className="text-xs text-texto-suave">
                {interacao.usuaria}
                {interacao.houveResposta ? ' · Respondeu' : ''}
              </p>
            </li>
          ))}
        </Bloco>
      </div>
    </section>
  );
}

function Bloco({
  teste,
  titulo,
  vazio,
  children,
}: {
  teste: string;
  titulo: string;
  vazio: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const itens = Array.isArray(children) ? children : [children];
  const vazioDeVerdade = itens.flat().filter(Boolean).length === 0;

  return (
    <div data-teste={teste} className="rounded-xl bg-superficie p-5 ring-1 ring-linha">
      <h2 className="font-semibold text-marinho">{titulo}</h2>
      {vazioDeVerdade ? (
        <p className="mt-2 text-sm text-texto-suave">{vazio}</p>
      ) : (
        <ul className="mt-3 space-y-2">{children}</ul>
      )}
    </div>
  );
}

/**
 * Correção do contrato pela Administradora.
 *
 * O dia de vencimento vale para qualquer contrato: não há outro lugar para
 * escrevê-lo. O valor mensal só aparece no Contrato herdado, que entra com
 * valor desconhecido e não tem Implantação; nos outros, o valor se corrige na
 * Implantação, onde conversa com o primeiro boleto.
 */
function EditarContrato({
  contatoId,
  contrato,
}: {
  contatoId: string;
  contrato: Visao360['contratos'][number];
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: (corpo: { valorMensal?: string; diaVencimento: number | null }) =>
      chamarApi(`/contratos/${contrato.id}`, { metodo: 'PATCH', corpo }),
    onSuccess: async () => {
      setAberto(false);
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['visao-360', contatoId] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['carteira'] });
      await clienteDeConsultas.invalidateQueries({ queryKey: ['renovacoes'] });
    },
    onError: (causa) =>
      setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível salvar o contrato.'),
  });

  function aoSalvar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const campos = new FormData(evento.currentTarget);
    const valor = String(campos.get('valorMensal') ?? '').trim();
    const dia = String(campos.get('diaVencimento') ?? '').trim();
    salvar.mutate({
      ...(valor ? { valorMensal: valor } : {}),
      diaVencimento: dia ? Number(dia) : null,
    });
  }

  if (!aberto) {
    return (
      <button
        type="button"
        onClick={() => setAberto(true)}
        className="mt-2 text-xs font-[750] text-marinho-claro underline"
      >
        {contrato.herdado ? 'Editar valor e vencimento' : 'Editar vencimento'}
      </button>
    );
  }

  return (
    <form onSubmit={aoSalvar} data-teste="editar-contrato" className="mt-2 grid gap-2 sm:grid-cols-2">
      {contrato.herdado ? (
        <label className="text-xs">
          Valor mensal (R$)
          <input
            name="valorMensal"
            inputMode="decimal"
            placeholder="Ex.: 1.500,00"
            defaultValue={
              contrato.valorMensalCentavos > 0
                ? (contrato.valorMensalCentavos / 100).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                  })
                : ''
            }
            className="mt-1 w-full rounded-lg border border-linha bg-superficie px-2 py-1.5 text-sm"
          />
        </label>
      ) : null}
      <label className="text-xs">
        Dia de vencimento
        <input
          name="diaVencimento"
          type="number"
          min={1}
          max={31}
          defaultValue={contrato.diaVencimento ?? ''}
          className="mt-1 w-full rounded-lg border border-linha bg-superficie px-2 py-1.5 text-sm"
        />
      </label>
      {erro ? (
        <p role="alert" className="text-xs text-erro sm:col-span-2">
          {erro}
        </p>
      ) : null}
      <div className="flex gap-2 sm:col-span-2">
        <button
          type="submit"
          disabled={salvar.isPending}
          className="rounded-[9px] bg-marinho px-3 py-1.5 text-xs font-[750] text-white disabled:opacity-60"
        >
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          onClick={() => {
            setAberto(false);
            setErro(null);
          }}
          className="rounded-[9px] border border-linha px-3 py-1.5 text-xs font-[750]"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

/**
 * As observações do Contato, com a edição na própria linha.
 *
 * Mora aqui, e não na janela da Oportunidade, porque os clientes da carteira
 * importada não aparecem na lista de Sales (ADR 0015): a janela nunca abre
 * para eles, e esta ficha é onde eles existem. O texto que a importação
 * escreveu ("Ramo de atividade: …") abre no campo, e salvar sem mexer não o
 * altera.
 *
 * O botão aparece sempre: quem ainda não tem observação recebe "Adicionar".
 * O campo só existe depois de clicar, então ele abre com o texto que a ficha
 * já carregou — nunca vazio por pressa, que é como uma anotação se perde.
 */
function ObservacoesDoContato({
  contatoId,
  observacoes,
}: {
  contatoId: string;
  observacoes: string | null;
}): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = useMutation({
    mutationFn: (texto: string) =>
      chamarApi(`/contatos/${contatoId}/observacoes`, {
        metodo: 'PATCH',
        corpo: { observacoes: texto },
      }),
    onSuccess: async () => {
      setAberto(false);
      setErro(null);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['visao-360', contatoId] });
    },
    onError: (causa) => {
      // A recusa do teto sobe com a mensagem própria, escrita em português
      // pela API; as outras validações trazem o texto padrão do zod.
      const dasObservacoes =
        causa instanceof ErroDaApi
          ? causa.problemas.find((problema) => problema.campo === 'observacoes')
          : undefined;

      setErro(
        dasObservacoes?.mensagem ??
          (causa instanceof ErroDaApi ? causa.message : 'Não foi possível salvar as observações.'),
      );
    },
  });

  function aoSalvar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const campos = new FormData(evento.currentTarget);
    salvar.mutate(String(campos.get('observacoes') ?? ''));
  }

  if (!aberto) {
    return (
      <div className="mt-1">
        {observacoes ? (
          <p
            data-teste="observacoes-do-contato"
            className="whitespace-pre-line text-xs text-texto-suave"
          >
            {observacoes}
          </p>
        ) : null}
        <button
          type="button"
          data-teste="editar-observacoes"
          onClick={() => setAberto(true)}
          className="mt-1 text-xs font-[750] text-marinho-claro underline"
        >
          {observacoes ? 'Editar observações' : 'Adicionar observações'}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={aoSalvar}
      data-teste="formulario-observacoes"
      className="mt-2 w-[560px] max-w-full"
    >
      <CampoTextoLongo
        rotulo="Observações"
        name="observacoes"
        rows={5}
        defaultValue={observacoes ?? ''}
        dica={`Cole aqui o que souber do cliente. Até ${TETO_DAS_OBSERVACOES.toLocaleString('pt-BR')} caracteres.`}
      />
      {erro ? (
        <p role="alert" className="mt-2 text-xs text-erro">
          {erro}
        </p>
      ) : null}
      <div className="mt-2 flex gap-2">
        <button
          type="submit"
          disabled={salvar.isPending}
          className="rounded-[9px] bg-marinho px-3 py-1.5 text-xs font-[750] text-white disabled:opacity-60"
        >
          {salvar.isPending ? 'Salvando…' : 'Salvar'}
        </button>
        <button
          type="button"
          onClick={() => {
            setAberto(false);
            setErro(null);
          }}
          className="rounded-[9px] border border-linha px-3 py-1.5 text-xs font-[750]"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
